// app/features/industry-opportunities/components/OpportunityProblemSectionComponent.tsx
'use client';

import { AlertTriangle,Clock, DollarSign } from 'lucide-react';

import type { IndustryOpportunity } from '../industry-opportunity.interface';

interface OpportunityProblemSectionComponentProps {
  opportunity: IndustryOpportunity;
}

// Parse the problem_impact field: "Revenue: ... | Cost: ... | Risk: ..."
function parseImpact(impact: string | null): { revenue?: string; cost?: string; risk?: string } {
  if (!impact) return {};
  
  const result: { revenue?: string; cost?: string; risk?: string } = {};
  const sections = impact.split('|').map(s => s.trim());
  
  for (const section of sections) {
    if (section.toLowerCase().startsWith('revenue:')) {
      result.revenue = section.replace(/^revenue:\s*/i, '').trim();
    } else if (section.toLowerCase().startsWith('cost:')) {
      result.cost = section.replace(/^cost:\s*/i, '').trim();
    } else if (section.toLowerCase().startsWith('risk:')) {
      result.risk = section.replace(/^risk:\s*/i, '').trim();
    }
  }
  
  return result;
}

export function OpportunityProblemSectionComponent({ 
  opportunity 
}: OpportunityProblemSectionComponentProps) {
  if (!opportunity.problem_description) {
    return null;
  }

  const impact = parseImpact(opportunity.problem_impact);
  const hasImpact = impact.revenue || impact.cost || impact.risk;

  return (
    <div className="py-12 lg:py-16 bg-beige-light">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-6">
            The Problem
          </h2>
          
          <div className="mb-10 space-y-5">
            {opportunity.problem_description.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-xl text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Impact Stats */}
          {hasImpact && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impact.revenue && (
                <div className="flex items-start gap-3">
                  <DollarSign size={24} className="text-green flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase text-gray-800 font-semibold mb-1">Revenue Impact</p>
                    <p className="text-gray-700">{impact.revenue}</p>
                  </div>
                </div>
              )}
              {impact.cost && (
                <div className="flex items-start gap-3">
                  <Clock size={24} className="text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase text-gray-800 font-semibold mb-1">Cost Impact</p>
                    <p className="text-gray-700">{impact.cost}</p>
                  </div>
                </div>
              )}
              {impact.risk && (
                <div className="flex items-start gap-3">
                  <AlertTriangle size={24} className="text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase text-gray-800 font-semibold mb-1">Risk</p>
                    <p className="text-gray-700">{impact.risk}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

